import React from "react";
import { connect } from "react-redux";
import moment from "moment";
import api from "../../utils/api";
import toastMessage from "../../utils/toastMessage";
import ReusableDashboard from "../Dashboard/ReusableDashboard";
import language from "../../language";
import { fetchShippingInQueueData } from "../../utils/queries/request/shipping-queue";
import { Modal } from "../Modal";
import { Table } from "../Table";
import { defaultChartColors } from "../Dashboard/DashboardChart";
import { capitalizeFirstChar } from "../Requests/RequestDashboard";

class BloodTestDashboard extends React.Component {
  state = {
    isLoading: false,
    error: {},
    data: {},
    requestsStatusCounts: {},
    filter: {
      startDate: moment().subtract(1, "months").format("YYYY-MM-DD"),
      endDate: moment().format("YYYY-MM-DD"),
      centers: [],
      centerSites: [],
      status: { value: "pending", label: "Pending" },
    },
    page: 1,
    limit: 10,
  };

  componentDidMount = async () => {
    await this.fetchData();
  };

  onFilterChange(field, value) {
    const _filters = this.state.filter;
    if (field === "date-range") {
      _filters.startDate = value.startDate;
      _filters.endDate = value.endDate;
    } else {
      _filters[field] = value;
    }

    this.setState({
      filter: { ..._filters },
    });
  }

  fetchData = async () => {
    await this.getData();
  };

  getData = async () => {
    this.setState({
      isLoading: false,
      serologyLoading: true,
      bloodTestStatsLoading: true,
    });

    const startDate =
      this.state.filter?.startDate ||
      moment().subtract(1, "months").format("YYYY-MM-DD");
    const endDate = this.state.filter?.endDate || moment().format("YYYY-MM-DD");
    const centers = (this.state.filter?.centers || []).map(
      ({ value }) => value
    );
    const centerSites = (this.state.filter?.centerSites || []).map(
      ({ value }) => value
    );

    const baseOptions = {
      method: "GET",
      params: {
        startDate,
        endDate,
        centers,
        centerSites,
      },
    };

    const requestId = Date.now();
    this.latestBloodTestGetDataId = requestId;

    const bloodTestStatsReq = api({
      ...baseOptions,
      url: `/bloodTest/blood-test-stats`,
    })
      .then(({ data }) => {
        if (this.latestBloodTestGetDataId !== requestId) return;
        this.setState({
          bloodTestStatsData: data,
          bloodTestStatsLoading: false,
        });
      })
      .catch((error) => {
        if (this.latestBloodTestGetDataId !== requestId) return;
        console.error("blood-test-stats error", error);
        toastMessage("error", error);
        this.setState({ bloodTestStatsLoading: false });
      });

    const serologyReq = api({
      ...baseOptions,
      url: `/bloodTest/donor-serology-count`,
    })
      .then(({ data }) => {
        if (this.latestBloodTestGetDataId !== requestId) return;
        this.setState({
          serologyData: data?.counts || {},
          serologyLoading: false,
        });
      })
      .catch((error) => {
        if (this.latestBloodTestGetDataId !== requestId) return;
        console.error("donor-serology-count error", error);
        toastMessage("error", error);
        this.setState({ serologyLoading: false });
      });

    await Promise.allSettled([bloodTestStatsReq, serologyReq]);

    if (this.latestBloodTestGetDataId === requestId) {
      this.setState({ isLoading: false });
    }
  };

  generateChartData = (raw) => {
    if (!raw || typeof raw !== "object") {
      return [["Products", "Count", { role: "style" }]];
    }

    const productEntries = Object.entries(raw).sort((a, b) =>
      a[0].localeCompare(b[0])
    );

    const data = [
      ["Products", "Count", { role: "style" }], // Header row with style
      ...productEntries.map(([field, count], index) => {
        const hue = (index * 360) / productEntries.length;
        const color = !!defaultChartColors[index]
          ? defaultChartColors[index]
          : `hsl(${hue}, 70%, 50%)`; // Generate unique color
        return [field, Number(count), color];
      }),
    ];

    return data;
  };

  handleShowModal(modal, selectedData) {
    this.setState(
      {
        [modal]: true,
        selectedData,
      },
      () => {
        if (modal === "showModal") {
          this.getDataByProductType(true, {
            product: selectedData.productType,
          });
        }
      }
    );
  }

  getDataByProductType = async (isLoading, search = {}) => {
    try {
      const { defaultLanguage } = this.props;

      this.setState({ isLoadingDataPerProductType: isLoading });

      const startDate =
        this.state.filter?.startDate ||
        moment().subtract(1, "months").format("YYYY-MM-DD");
      const endDate =
        this.state.filter?.endDate || moment().format("YYYY-MM-DD");
      const centers = (this.state.filter?.centers || []).map(
        ({ value }) => value
      );
      const centerSites = (this.state.filter?.centerSites || []).map(
        ({ value }) => value
      );

      const { data, count, totalRecords } = await fetchShippingInQueueData(defaultLanguage, {
        ...search,
        page: this.state.page,
        limit: this.state.limit,
        startDate,
        endDate,
        centers,
        centerSites,
        status: this.state?.status,
      });

      this.setState({
        producttypeData: data,
        totalPageCount: count,
        totalRecords,
        isLoadingDataPerProductType: false,
      });
    } catch (error) {
      this.setState({
        isLoadingDataPerProductType: false,
      });
      toastMessage("error", error);
    }
  };

  handlePagination(page, _tab, limit) {
    this.setState(
      {
        page,
        ...(limit != null && { limit }),
      },
      () => {
        this.getDataByProductType(true);
      }
    );
  }

  handleRemoteSearch = async (search) => {
    await new Promise((r) => this.setState({ searching: true, page: 1 }, r));
    await this.getDataByProductType(true, {
      donationNumber: search || search !== "" ? search : undefined,
    });

    this.setState({
      searching: false,
    });
  };

  handleSearch = async (e) => {
    const search = e?.target?.value;
    this.setState({ search_text: search });
  };

  handleCloseModal(modal) {
    this.setState({
      [modal]: false,
    });
  }

  render() {
    return (
      <>
        <ReusableDashboard
          loading={this.state.isLoading}
          filtersConfig={{
            hasCenters: true,
            hasSites: true,
            filters: this.state.filter,
            onFilterChange: (field, value) => this.onFilterChange(field, value),
            // customFilter: [statusFilter],
          }}
          onFind={() => this.fetchData()}
          cardGroups={[
            {
              title: "Blood Tests Stats",
              color: defaultChartColors[0],
              col: 3,
              isLoading: this.state.bloodTestStatsLoading,
              items: [
                {
                  title: "Testing Rate",
                  count: this.state.bloodTestStatsData?.testingRate || 0,
                },
                ...Object.keys(this.state.bloodTestStatsData || {})
                  .filter((item) => item !== "testingRate")
                  .map((key) => {
// eslint-disable-next-line no-unused-vars
                    const canPress =
                      (this.state.bloodTestStatsData[key] || 0) > 0;
                    return {
                      title: capitalizeFirstChar(key),
                      count: this.state.bloodTestStatsData[key] || 0,
                      // onPress:
                      //   !!canPress &&
                      //   (() => {
                      //     this.handleShowModal(
                      //       "showModal",
                      //       "bloodTestStats",
                      //       key
                      //     );
                      //     this.getDonationByAssessment(
                      //       "bloodTestStats",
                      //       key
                      //     );
                      //   }),
                    };
                  }),
              ],
            },
          ]}
          charts={[
            {
              id: "00012",
              title: `Donor with TTI`,
              type: "ColumnChart",
              isLoading: this.state.serologyLoading,
              data: this.generateChartData(this.state.serologyData),
              legend: "right",
              height: "400px",
              col: 12,
            },
          ]}
        />

        <Modal
          show={this.state.showModal}
          handleClose={() => this.handleCloseModal("showModal")}
          title={this.state.selectedData?.productTypeName}
          size="lg"
        >
          <Table
            data={this.state.producttypeData}
            isSearch
            page={this.state.page}
            limit={this.state.limit}
            addSearchButton
            search_text={this.state.search_text}
            searching={this.state.searching}
            handleSearch={(e) => this.onChangeText("search_text", e)}
            handlePressSearch={() =>
              this.handleRemoteSearch(this.state.search_text)
            }
            totalPageCount={this.state.totalPageCount}
            totalRecords={this.state.totalRecords}
            isLoading={this.state.isLoadingDataPerProductType}
            handlePagination={this.handlePagination.bind(this)}
            headers={[
              {
                title: language[this.props.defaultLanguage].donation_number,
                key: "donationNumber",
              },
              {
                title: language[this.props.defaultLanguage].source,
                key: "source",
              },
              {
                title: language[this.props.defaultLanguage].destination,
                key: "destination",
              },
              {
                title: language[this.props.defaultLanguage].status,
                key: "status",
                type: "status",
              },
              {
                title:
                  language[this.props.defaultLanguage]
                    .product_verified_shipped_by,
                key: "addedBy",
                styles: {
                  textTransform: "uppercase",
                },
              },
              {
                title: language[this.props.defaultLanguage].date,
                key: "createdAt",
                formatTime: "lll",
                isMoment: true,
              },
            ]}
          />
        </Modal>
      </>
    );
  }
}

const mapStateToProps = (state) => {
  const { defaultLanguage } = state.Language;
  const { filters } = state.Filters;
  return {
    defaultLanguage,
    filters,
  };
};

export default connect(mapStateToProps)(BloodTestDashboard);
